import { Fragment } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Page from '../../components/Page'
import Prompt from '../../components/Prompt'
import useContentful from '../../contexts/useContentful'
import { ReactComponent as IconCheckmark } from '../../icons/Checkmark.svg'
import { SURVEY_ID } from '../../data/surveyID'
import TopicsDisplay from '../../components/TopicsDisplay'
// import useRecaptcha from "../../hooks/useRecaptcha";

const ChooseTopicPage = () => {
  const { surveyID, step } = useParams()
  const {
    bodyTitle,
    pageDescription,
    pageTitle,
    section1ButtonText,
    section1Label,
    section1StepImages,
    section1Steps,
    section1Title,
    section2ButtonText,
    section2Label,
    section2StepImages,
    section2StepImagesInactive,
    section2Steps,
    section2Title,
    submittedLabel,
  } = useContentful()
  const activeStep = +step || 0
  // const { executeRecaptcha } = useRecaptcha();

  const handleButtonClick = () => {
    // const actionName = activeStep !== 1 ? "Part_1_Intro" : "Part_2_Intro";
    // const recapResult = executeRecaptcha(actionName);
  };

  return (
    <Page>
      <Stack>
        <Container>
          <Stack
            display="grid"
            py={6}
            gap={6}
          >
            {activeStep !== 1 && (
              <Fragment>
                <Stack
                  display="grid"
                  gap={4}
                  textAlign="center"
                  justifySelf="center"
                  justifyItems="center"
                >
                  <Typography
                    variant="h2"
                    sx={{
                      maxWidth: 750,
                    }}
                  >
                    {pageTitle}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.neutralDark"
                    sx={{
                      maxWidth: 650,
                    }}
                  >
                    {pageDescription}
                  </Typography>
                </Stack>
                <Divider
                  light
                />
              </Fragment>
            )}
            <Stack
              display="grid"
              gap={6}
            >
              {activeStep !== 1 && (
                <Stack
                  display="grid"
                >
                  <Typography
                    variant="bigLabel"
                    textAlign="center"
                    sx={{
                      maxWidth: 650,
                      justifySelf: 'center',
                    }}
                    mb={4}
                  >
                    {bodyTitle}
                  </Typography>
                  <TopicsDisplay />
                </Stack>
              )}
              <Stack
                display="grid"
                gap={4}
                gridTemplateColumns={{
                  md: 'repeat(2, 1fr)',
                }}
              >
                <Paper
                  className={`drawn ${activeStep === 0 ? 'active' : 'inactive'}`}
                  sx={{
                    width: '100%',
                    display: 'inline-grid',
                  }}
                >
                  <Stack
                    // display="grid"
                    // gridTemplateRows={activeStep === 1 ? 'auto 1fr' : undefined}
                    p={2}
                    gap={4}
                    textAlign="center"
                  >
                    <Stack
                      gap={1}
                    >
                      <Typography
                        variant="body1Medium"
                        color="text.neutralDark"
                      >
                        {section1Label}
                      </Typography>
                      <Typography
                        variant="bigLabel"
                        color="text.neutralDark"
                      >
                        {section1Title}
                      </Typography>
                    </Stack>
                    {activeStep === 0 && (
                      <>
                        <Divider />
                        <Stack
                          display="grid"
                          justifyContent="center"
                          justifyItems="center"
                          gap={{
                            xs: 3,
                            sm: 1.5,
                          }}
                          gridTemplateColumns={{
                            sm: `repeat(${section1Steps?.length}, 1fr)`,
                          }}
                        >
                          {section1Steps?.map((o, idx) => (
                            <Stack
                              key={section1StepImages?.[idx]?.fields?.title}
                              display="inline-grid"
                              gap={{
                                xs: 1.5,
                                sm: 2.5,
                              }}
                              alignContent="flex-start"
                              justifyItems="center"
                              textAlign="center"
                              maxWidth="175px"
                            >
                              <img 
                                src={section1StepImages?.[idx]?.fields?.file?.url} 
                                alt={section1StepImages?.[idx]?.fields?.title}
                                className="step-icon"
                              />
                              <Typography
                                variant="subtitle2"
                                color="text.neutralDark"
                              >
                                {o}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                        <RouterLink
                          to={surveyID === SURVEY_ID.SLOW_NO_GROWTH ? `/rural/${surveyID}` : `/topic/${surveyID}/housing`}
                        >
                          <Button onClick={handleButtonClick}>
                            {section1ButtonText}
                          </Button>
                        </RouterLink>
                      </>
                    )}
                    {activeStep !== 0 && (
                      <Stack
                        display="grid"
                        gap={2}
                        justifyItems="center"
                        alignContent="flex-start"
                        textAlign="center"
                      >
                        <IconCheckmark />
                        <Typography
                          variant="bigLabel"
                          color="text.neutralDark"
                        >
                          {submittedLabel}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
                <Paper
                  className={`drawn ${activeStep === 1 ? 'active' : 'inactive'}`}
                  sx={{
                    width: '100%',
                    display: 'inline-grid',
                  }}
                >
                  <Stack
                    // display="grid"
                    p={2}
                    gap={4}
                    textAlign="center"
                  >
                    <Stack
                      gap={1}
                    >
                      <Typography
                        variant="body1Medium"
                        color="text.neutralDark"
                      >
                        {section2Label}
                      </Typography>
                      <Typography
                        variant="bigLabel"
                        color="text.neutralDark"
                      >
                        {section2Title}
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack
                      display="grid"
                      justifyContent="center"
                      justifyItems="center"
                      gap={{
                        xs: 3,
                        sm: 1.5,
                      }}
                      gridTemplateColumns={{
                        sm: `repeat(${section2Steps?.length}, 1fr)`,
                      }}
                    >
                      {section2Steps?.map((o, idx) => (
                        <Stack
                          key={activeStep === 1 ? section2StepImages?.[idx]?.fields?.title : section2StepImagesInactive?.[idx]?.fields?.title}
                          display="inline-grid"
                          gap={2.5}
                          alignContent="flex-start"
                          justifyItems="center"
                          textAlign="center"
                          maxWidth="175px"
                        >
                          <img 
                            src={activeStep === 1 ? section2StepImages?.[idx]?.fields?.file?.url : section2StepImagesInactive?.[idx]?.fields?.file?.url} 
                            alt={activeStep === 1 ? section2StepImages?.[idx]?.fields?.title : section2StepImagesInactive?.[idx]?.fields?.title}
                            className="step-icon"
                          />
                          <Typography
                            variant="subtitle2"
                            color="text.neutralDark"
                          >
                            {o}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                    {activeStep === 1 && (
                      <RouterLink
                        to={`/big-ideas/${surveyID}/housing`}
                      >
                        <Button>
                          {section2ButtonText}
                        </Button>
                      </RouterLink>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Stack>
      <Prompt />
    </Page>
  )
}

export default ChooseTopicPage
